import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { WheelService } from '../wheel.service';
import { CostComponent } from '../../../core/components/cost/cost.component';
import { CountUpModule } from 'ngx-countup';
import { ContextMenuService } from '@core/services/context-menu.service';
import { listAnimation } from '@core/utils/animations.util';
import { WheelColor } from '../interfaces/wheel.interface';
import NumberFlow from 'number-flow';
import { SvgIconComponent } from 'angular-svg-icon';
import { sleep } from '@core/utils/sleep.util';

@Component({
  selector: 'app-wheel-color-row',
  templateUrl: './wheel-color-row.component.html',
  styleUrls: ['./wheel-color-row.component.scss'],
  standalone: true,
  imports: [CostComponent, CountUpModule, SvgIconComponent],
  animations: [listAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class WheelColorRowComponent {
  public readonly wheelService = inject(WheelService);
  public readonly contextMenuService = inject(ContextMenuService);

  betsRef = viewChild.required<ElementRef<NumberFlow>>('bets');
  totalRef = viewChild.required<ElementRef<NumberFlow>>('total');
  totalWonRef = viewChild.required<ElementRef<NumberFlow>>('totalWon');
  totalWonContainerRef =
    viewChild.required<ElementRef<HTMLDivElement>>('totalWonContainer');

  public readonly color = input.required<WheelColor>();

  public isLose = signal(false);

  public isWon = signal(false);

  private updateIsWonLoseEffect = afterRenderEffect(async () => {
    const { status, winningColor } = this.wheelService;
    // return status() === 'results' && winningColor() === this.color();

    if (status() === 'results') {
      const isColorWinner = winningColor() === this.color();
      this.isWon.set(isColorWinner);
      this.isLose.set(!isColorWinner);
    }

    if (status() === 'running') {
      await sleep(400);
      this.isWon.set(false);
      this.isLose.set(false);
    }
  });

  private updateBetsEffect = afterRenderEffect(() => {
    const total = this.wheelService.betsList()[this.color()].length;
    this.betsRef().nativeElement.update(total);
  });

  private updateTotalEffect = afterRenderEffect(() => {
    const total = this.wheelService.betsValue()[this.color()];
    this.totalRef().nativeElement.update(total);
  });

  public totalWon = computed(() => {
    const colorList = this.wheelService.betsList()[this.color()];
    const totalSum = colorList.reduce((acc, bet) => acc + bet.amount, 0);
    const wonMultiplier = this.wheelService.wheelMultipliers[this.color()];
    const totalWonWithMultipliers = totalSum * wonMultiplier;
    return this.isWon() ? totalWonWithMultipliers : totalSum;
  });

  private wonEffect = afterRenderEffect(async () => {
    if (this.wheelService.status() === 'results') {
      this.totalWonContainerRef().nativeElement.classList.add('animate-in');
      await sleep(400);
      this.totalWonRef().nativeElement.update(this.totalWon());
    }
  });

  private resetEffect = afterRenderEffect(async () => {
    const { status } = this.wheelService;
    const totalWonContainer = this.totalWonContainerRef().nativeElement;
    if (
      status() === 'running' &&
      totalWonContainer.classList.contains('animate-in')
    ) {
      this.totalWonRef().nativeElement.update(0);
      totalWonContainer.classList.add('animate-out');
      await sleep(600);
      totalWonContainer.classList.remove('animate-in', 'animate-out');
    }
  });

  private readonly numberFlowSettings = {
    format: {
      minimumIntegerDigits: 1,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    locales: 'en-US',
    transformTiming: {
      duration: 300,
      //easing: 'linear',
    },
    spinTiming: {
      duration: 300,
      //easing: 'linear',
    },
    opacityTiming: {
      duration: 300,
      //easing: 'linear',
    },
  };

  ngAfterViewInit() {
    const bets = this.betsRef().nativeElement;
    const total = this.totalRef().nativeElement;
    const totalWon = this.totalWonRef().nativeElement;

    bets.transformTiming = this.numberFlowSettings.transformTiming;
    bets.spinTiming = this.numberFlowSettings.spinTiming;
    bets.opacityTiming = this.numberFlowSettings.opacityTiming;

    total.transformTiming = this.numberFlowSettings.transformTiming;
    total.spinTiming = this.numberFlowSettings.spinTiming;
    total.opacityTiming = this.numberFlowSettings.opacityTiming;

    totalWon.transformTiming = this.numberFlowSettings.transformTiming;
    totalWon.spinTiming = this.numberFlowSettings.spinTiming;
    totalWon.opacityTiming = this.numberFlowSettings.opacityTiming;

    total.format = this.numberFlowSettings.format;
    totalWon.format = this.numberFlowSettings.format;

    bets.update(this.wheelService.betsList()[this.color()].length);
    total.update(this.wheelService.betsValue()[this.color()]);
    totalWon.update(this.totalWon());
  }

  public clickBet() {
    this.wheelService.bet(this.color());
  }
}
